import { useEffect } from "react";

const EDITABLE_SELECTOR = [
  "input:not([type='checkbox']):not([type='radio']):not([type='range']):not([type='file']):not([type='button']):not([type='submit']):not([type='reset'])",
  "textarea",
  "select",
  "[contenteditable='true']",
].join(",");

function isIosDevice() {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent ?? "";
  const isClassicIos = /iPad|iPhone|iPod/.test(userAgent);
  const isIpadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

  return isClassicIos || isIpadOs;
}

function getViewportMeta() {
  const existing = document.querySelector<HTMLMetaElement>("meta[name='viewport']");
  if (existing) return existing;

  const meta = document.createElement("meta");
  meta.name = "viewport";
  meta.content = "width=device-width, initial-scale=1";
  document.head.appendChild(meta);
  return meta;
}

function buildViewportContent(baseContent: string, lockScale: boolean) {
  const parts = baseContent
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const key = part.split("=")[0]?.trim().toLowerCase();
      return key !== "maximum-scale" && key !== "minimum-scale";
    });

  if (lockScale) {
    parts.push("maximum-scale=1");
  }

  return parts.join(", ");
}

function isEditableTarget(target: EventTarget | null) {
  return target instanceof Element && target.matches(EDITABLE_SELECTOR);
}

export function MobileInputFocusGuard() {
  useEffect(() => {
    if (typeof window === "undefined" || !isIosDevice()) {
      return;
    }

    const viewportMeta = getViewportMeta();
    const baseContent = viewportMeta.content || "width=device-width, initial-scale=1";
    const lockedContent = buildViewportContent(baseContent, true);

    const lockViewport = () => {
      viewportMeta.content = lockedContent;
    };

    const unlockViewport = () => {
      viewportMeta.content = baseContent;
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isEditableTarget(event.target)) return;
      lockViewport();
    };

    const handleFocusOut = () => {
      window.requestAnimationFrame(() => {
        if (isEditableTarget(document.activeElement)) return;
        unlockViewport();
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        unlockViewport();
      }
    };

    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);
    window.addEventListener("pagehide", unlockViewport);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unlockViewport();
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
      window.removeEventListener("pagehide", unlockViewport);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return null;
}
